import { useEffect, useState } from "react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ensurePushSubscription } from "@/lib/push";
import { Button } from "@/components/ui/button";
import BrandLogo from "@/components/BrandLogo";
import Avatar from "@/components/Avatar";
import NotificationBell from "@/components/NotificationBell";
import ImageCropper from "@/components/ImageCropper";
import { UserCheck, LogOut, School, Plus, FileText, Upload, Trash2, User as UserIcon, MessageSquare } from "lucide-react";
import MobileNav from "@/components/MobileNav";
import { toast } from "sonner";

export default function TrainerDashboard() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [classes, setClasses] = useState([]);
    const [topicsCount, setTopicsCount] = useState(0);

    useEffect(() => {
        api.get("/trainer/classes").then((r) => setClasses(r.data)).catch(() => setClasses([]));
        api.get("/topics").then((r) => setTopicsCount((r.data || []).filter((t) => t.trainer_id === user?.id).length)).catch(() => {});
        ensurePushSubscription();
    }, [user?.id]);

    return (
        <div dir="rtl" className="min-h-screen bg-slate-50">
            <header className="bg-[#0F172A] text-white sticky top-0 z-10">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-2">
                    <BrandLogo size={40} />
                    <MobileNav
                        title="القائمة"
                        inlineExtras={<NotificationBell onMessages={() => nav("/messages")} />}
                        items={[
                            { label: "حسابي", icon: UserIcon, onClick: () => nav("/profile"), testid: "btn-profile" },
                            { label: "الرسائل", icon: MessageSquare, onClick: () => nav("/messages"), testid: "btn-open-messages" },
                            { label: "موضوعاتي", icon: FileText, onClick: () => nav("/trainer/topics"), testid: "btn-my-topics-nav" },
                            { label: "إضافة موضوع جديد", icon: Plus, onClick: () => nav("/trainer/topics/new"), testid: "btn-new-topic-nav" },
                            { label: "تسجيل الخروج", icon: LogOut, onClick: async () => { await logout(); nav("/login"); }, destructive: true, testid: "btn-logout" },
                        ]}
                    />
                </div>
            </header>
            <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
                <div className="bg-gradient-to-l from-[#1B2A4E] to-[#0F172A] text-white rounded-2xl p-8">
                    <div className="flex items-center gap-3 mb-2"><UserCheck className="w-6 h-6 text-[#C8102E]" /><span className="text-xs text-white/70 uppercase tracking-wider">لوحة المدربة</span></div>
                    <h1 className="text-2xl font-bold">أهلاً {user?.full_name}</h1>
                    <p className="text-white/70 mt-1 text-sm">تخصّص: {user?.specialization === "quantitative" ? "قدرات كمية" : user?.specialization === "verbal" ? "قدرات لفظية" : "كمية ولفظية"}</p>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                    <button
                        onClick={() => nav("/trainer/topics/new")}
                        data-testid="btn-quick-new-topic"
                        className="group text-right bg-white rounded-2xl border-2 border-dashed border-slate-300 hover:border-[#1B2A4E] p-6 flex items-center gap-4 hover:shadow-md transition-all"
                    >
                        <div className="w-14 h-14 rounded-xl bg-[#1B2A4E] text-white flex items-center justify-center group-hover:scale-105 transition-transform"><Plus className="w-6 h-6" /></div>
                        <div className="flex-1">
                            <div className="text-lg font-bold text-[#0F172A]">إضافة موضوع جديد</div>
                            <div className="text-xs text-slate-500">اكتبي، أرفقي، انشري لطالباتك</div>
                        </div>
                    </button>
                    <button
                        onClick={() => nav("/trainer/topics")}
                        data-testid="btn-my-topics"
                        className="group text-right bg-white rounded-2xl border border-slate-200 hover:border-[#C8102E] p-6 flex items-center gap-4 hover:shadow-md transition-all"
                    >
                        <div className="w-14 h-14 rounded-xl bg-[#FFF1F2] text-[#C8102E] flex items-center justify-center"><FileText className="w-6 h-6" /></div>
                        <div className="flex-1">
                            <div className="text-lg font-bold text-[#0F172A]">موضوعاتي</div>
                            <div className="text-xs text-slate-500 font-mono-num">{topicsCount} موضوع منشور</div>
                        </div>
                    </button>
                </div>

                <div>
                    <h2 className="text-lg font-semibold text-[#0F172A] mb-3">الفصول المسندة</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {classes.length === 0 && <div className="col-span-full text-center py-12 text-slate-400 bg-white rounded-xl border border-slate-200">لا توجد فصول مسندة بعد</div>}
                        {classes.map((c) => (
                            <ClassCard key={c.id} c={c} onChanged={() => api.get("/trainer/classes").then((r) => setClasses(r.data))} />
                        ))}
                    </div>
                </div>
            </main>
        </div>
    );
}

function ClassCard({ c, onChanged }) {
    const [busy, setBusy] = useState(false);
    const [cropOpen, setCropOpen] = useState(false);
    const [src, setSrc] = useState(null);
    const fileRef = React.useRef(null);
    const nav = useNavigate();
    const pick = (e) => {
        const f = e.target.files?.[0]; if (!f) return;
        const rd = new FileReader();
        rd.onload = () => { setSrc(rd.result); setCropOpen(true); };
        rd.readAsDataURL(f);
        if (fileRef.current) fileRef.current.value = "";
    };
    const upload = async (file) => {
        setBusy(true);
        try {
            const fd = new FormData(); fd.append("file", file);
            await api.post(`/classes/${c.id}/cover`, fd);
            toast.success("تم تحديث غلاف الفصل"); onChanged();
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    };
    const remove = async () => {
        try { await api.delete(`/classes/${c.id}/cover`); toast.success("تمت الإزالة"); onChanged(); } catch (e) { toast.error(formatApiError(e)); }
    };
    const BACKEND = process.env.REACT_APP_BACKEND_URL;
    return (
        <div
            className="bg-white rounded-xl border border-slate-200 overflow-hidden group relative cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all"
            data-testid={`class-card-${c.id}`}
            onClick={() => nav(`/trainer/class/${c.id}`)}
        >
            <div className="relative h-28 bg-gradient-to-l from-[#1B2A4E] to-[#0F172A] overflow-hidden">
                {c.cover_url && <img src={`${BACKEND}${c.cover_url}`} alt="" className="absolute inset-0 w-full h-full object-cover" />}
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute bottom-2 right-3 text-white font-bold text-lg drop-shadow">{c.name}</div>
                <div className="absolute top-2 left-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                    <button onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }} disabled={busy} className="w-8 h-8 rounded-lg bg-white/90 text-[#1B2A4E] flex items-center justify-center hover:bg-white" data-testid={`btn-cover-${c.id}`} title="غلاف الفصل"><Upload className="w-4 h-4" /></button>
                    {c.cover_url && <button onClick={(e) => { e.stopPropagation(); remove(); }} className="w-8 h-8 rounded-lg bg-white/90 text-[#C8102E] flex items-center justify-center hover:bg-white" title="إزالة الغلاف"><Trash2 className="w-4 h-4" /></button>}
                </div>
                <input ref={fileRef} type="file" hidden accept="image/*" onChange={pick} onClick={(e) => e.stopPropagation()} />
            </div>
            <div className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-2 text-[#C8102E]"><School className="w-4 h-4" /><span className="text-xs text-slate-500 font-mono-num">{c.student_count} طالبة</span></div>
                <span className="text-xs text-[#1B2A4E] font-medium">افتحي الفصل ←</span>
            </div>
            <ImageCropper open={cropOpen} onOpenChange={setCropOpen} imageSrc={src} aspect={16/9} onCropped={upload} title={`غلاف ${c.name}`} />
        </div>
    );
}
